import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import AppImage from '../../../components/AppImage';

const HeroSection = ({ onHireClick, onContactClick }) => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({
        x: (e?.clientX / window.innerWidth - 0.5) * 20,
        y: (e?.clientY / window.innerHeight - 0.5) * 20
      });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: [0.6, 0.05, 0.01, 0.9]
      }
    }
  };

  const imageVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 1,
        ease: [0.6, 0.05, 0.01, 0.9]
      }
    }
  };

  const floatingVariants = {
    animate: {
      y: [0, -20, 0],
      transition: {
        duration: 3,
        repeat: Infinity,
        ease: "easeInOut"
      }
    }
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden px-4 md:px-6 lg:px-8 pt-24 md:pt-28 lg:pt-32 pb-12 md:pb-16 lg:pb-20">
      {/* Enhanced Animated Background with Multiple Gradients */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-teal-950 to-cyan-950" />
      <motion.div
        className="absolute inset-0 opacity-40"
        style={{
          background: 'radial-gradient(circle at 30% 20%, rgba(20, 184, 166, 0.4) 0%, transparent 50%), radial-gradient(circle at 70% 80%, rgba(6, 182, 212, 0.3) 0%, transparent 50%)',
          transform: `translate(${mousePosition?.x}px, ${mousePosition?.y}px)`
        }}
      />
      {/* Animated Gradient Orbs */}
      <motion.div
        className="absolute top-20 left-10 w-64 h-64 md:w-96 md:h-96 lg:w-[32rem] lg:h-[32rem] rounded-full opacity-30 blur-3xl"
        style={{
          background: 'radial-gradient(circle, rgba(20, 184, 166, 0.6) 0%, rgba(6, 182, 212, 0.4) 50%, transparent 100%)'
        }}
        animate={{
          scale: [1, 1.3, 1],
          opacity: [0.3, 0.5, 0.3],
          x: [0, 50, 0],
          y: [0, 30, 0]
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      />
      <motion.div
        className="absolute bottom-20 right-10 w-72 h-72 md:w-[28rem] md:h-[28rem] lg:w-[36rem] lg:h-[36rem] rounded-full opacity-30 blur-3xl"
        style={{
          background: 'radial-gradient(circle, rgba(245, 158, 11, 0.5) 0%, rgba(6, 182, 212, 0.3) 50%, transparent 100%)'
        }}
        animate={{
          scale: [1.2, 1, 1.2],
          opacity: [0.4, 0.2, 0.4],
          x: [0, -40, 0],
          y: [0, -30, 0]
        }}
        transition={{
          duration: 10,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      />
      {/* Grid Pattern Overlay */}
      <div className="absolute inset-0 opacity-10" style={{
        backgroundImage: 'linear-gradient(rgba(20, 184, 166, 0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(20, 184, 166, 0.3) 1px, transparent 1px)',
        backgroundSize: '50px 50px'
      }} />
      {/* Floating Icons with Enhanced Styling */}
      <motion.div
        className="absolute top-32 right-20 hidden lg:block"
        variants={floatingVariants}
        animate="animate"
      >
        <div className="w-16 h-16 lg:w-20 lg:h-20 rounded-2xl bg-gradient-to-br from-primary/30 to-secondary/20 backdrop-blur-md flex items-center justify-center border border-primary/30 shadow-xl">
          <Icon name="Code2" size={28} color="var(--color-primary)" />
        </div>
      </motion.div>
      <motion.div
        className="absolute bottom-40 left-20 hidden lg:block"
        variants={floatingVariants}
        animate="animate"
        transition={{ delay: 0.5 }}
      >
        <div className="w-16 h-16 lg:w-20 lg:h-20 rounded-2xl bg-gradient-to-br from-secondary/30 to-accent/20 backdrop-blur-md flex items-center justify-center border border-secondary/30 shadow-xl">
          <Icon name="Lightbulb" size={28} color="var(--color-secondary)" />
        </div>
      </motion.div>
      <motion.div
        className="absolute top-1/2 right-10 hidden md:block"
        variants={floatingVariants}
        animate="animate"
        transition={{ delay: 1 }}
      >
        <div className="w-14 h-14 md:w-16 md:h-16 lg:w-18 lg:h-18 rounded-2xl bg-gradient-to-br from-accent/30 to-primary/20 backdrop-blur-md flex items-center justify-center border border-accent/30 shadow-xl">
          <Icon name="Rocket" size={24} color="var(--color-accent)" />
        </div>
      </motion.div>
      {/* Main Content */}
      <motion.div
        className="relative z-10 max-w-7xl mx-auto"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left Column - Text Content */}
          <div className="text-center lg:text-left order-2 lg:order-1">
            {/* Greeting Badge */}
            <motion.div variants={itemVariants} className="mb-6 md:mb-8">
              <div className="inline-flex items-center gap-2 px-5 py-3 md:px-6 md:py-3.5 rounded-full bg-gradient-to-r from-primary/20 to-secondary/20 border border-primary/30 backdrop-blur-md shadow-lg">
                <motion.div
                  animate={{ rotate: [0, 10, -10, 0] }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  <Icon name="Sparkles" size={18} color="var(--color-primary)" />
                </motion.div>
                <span className="text-sm md:text-base font-semibold bg-gradient-to-r from-primary via-secondary to-accent bg-clip-text text-transparent">
                  Welcome to Innovation & Excellence
                </span>
              </div>
            </motion.div>

            {/* Name Display */}
            <motion.h1
              variants={itemVariants}
              className="text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-bold mb-6 md:mb-8 leading-tight"
            >
              <motion.span
                className="inline-block bg-gradient-to-r from-primary via-secondary to-accent bg-clip-text text-transparent"
                animate={{
                  backgroundPosition: ['0% 50%', '100% 50%', '0% 50%']
                }}
                transition={{
                  duration: 5,
                  repeat: Infinity,
                  ease: "linear"
                }}
                style={{ backgroundSize: '200% 200%' }}
              >
                Edrian Bass
              </motion.span>
            </motion.h1>

            {/* Dynamic Tagline */}
            <motion.div variants={itemVariants} className="mb-8 md:mb-10">
              <h2 className="text-xl md:text-2xl lg:text-3xl font-semibold text-foreground mb-4 md:mb-6">
                <span className="text-primary">IT Engineer</span> • <span className="text-secondary">Entrepreneur</span> • <span className="text-accent">Multimedia Professional</span> • <span className="text-primary">Creative Director</span>
              </h2>
              <p className="text-base md:text-lg lg:text-xl text-muted-foreground leading-relaxed">
                Transforming ideas into digital excellence through innovative technology solutions, strategic business guidance, and creative media production. Managing TV networks and building the future, one line of code and one inspired mind at a time.
              </p>
            </motion.div>

            {/* CTA Buttons */}
            <motion.div
              variants={itemVariants}
              className="flex flex-col sm:flex-row items-center lg:items-start lg:justify-start justify-center gap-4 md:gap-5 mb-10 md:mb-12"
            >
              <motion.div
                whileHover={{ scale: 1.05, boxShadow: '0 20px 40px rgba(99, 102, 241, 0.4)' }}
                whileTap={{ scale: 0.95 }}
                className="w-full sm:w-auto"
              >
                <Button
                  variant="default"
                  size="lg"
                  onClick={onHireClick}
                  iconName="Briefcase"
                  iconPosition="right"
                  className="w-full sm:w-auto text-base md:text-lg lg:text-xl px-8 md:px-10 lg:px-12 py-4 md:py-5 lg:py-6 shadow-2xl bg-gradient-to-r from-primary to-secondary hover:from-primary/90 hover:to-secondary/90 border-0"
                >
                  Hire Me
                </Button>
              </motion.div>

              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="w-full sm:w-auto"
              >
                <Button
                  variant="outline"
                  size="lg"
                  onClick={onContactClick}
                  iconName="MessageCircle"
                  iconPosition="right"
                  className="w-full sm:w-auto text-base md:text-lg lg:text-xl px-8 md:px-10 lg:px-12 py-4 md:py-5 lg:py-6 border-2 border-primary/50 hover:bg-primary/10 backdrop-blur-sm"
                >
                  Contact
                </Button>
              </motion.div>
            </motion.div>
          </div>

          {/* Right Column - Professional Portrait */}
          <motion.div
            variants={imageVariants}
            className="order-1 lg:order-2 flex justify-center lg:justify-end"
          >
            <div className="relative">
              {/* Glowing Background Effect */}
              <motion.div
                className="absolute inset-0 bg-gradient-to-br from-primary via-secondary to-accent rounded-3xl blur-3xl opacity-50"
                animate={{
                  scale: [1, 1.1, 1],
                  opacity: [0.4, 0.6, 0.4]
                }}
                transition={{
                  duration: 4,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              />
              
              {/* Image Container with Glass Morphism */}
              <motion.div
                className="relative w-64 h-64 md:w-80 md:h-80 lg:w-96 lg:h-96 xl:w-[28rem] xl:h-[28rem] rounded-3xl overflow-hidden border-4 border-primary/30 shadow-2xl"
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.3 }}
              >
                <AppImage
                  src="/assets/images/WhatsApp_Image_2026-01-14_at_10.10.46_AM-1768414282695.jpeg"
                  alt="Edrian Bass - Professional Portrait"
                  className="w-full h-full object-cover"
                />
                
                {/* Decorative Corner Elements */}
                <div className="absolute top-4 left-4 w-12 h-12 border-t-4 border-l-4 border-primary rounded-tl-2xl z-20" />
                <div className="absolute bottom-4 right-4 w-12 h-12 border-b-4 border-r-4 border-secondary rounded-br-2xl z-20" />
              </motion.div>
              
              {/* Floating Badge */}
              <motion.div
                className="absolute -bottom-6 -right-6 bg-gradient-to-br from-accent to-primary px-6 py-4 rounded-2xl shadow-2xl border-2 border-accent/50"
                animate={{
                  y: [0, -10, 0]
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              >
                <div className="flex items-center gap-2">
                  <Icon name="Award" size={24} color="white" />
                  <div className="text-white">
                    <div className="text-xs font-medium opacity-90">Available for</div>
                    <div className="text-sm font-bold">New Projects</div>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>

        {/* Stats Counter - Full Width Below */}
        <motion.div
          variants={itemVariants}
          className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 lg:gap-8 mt-16 md:mt-20 lg:mt-24"
        >
          {[
            { value: '150+', label: 'Projects Completed', icon: 'CheckCircle2' },
            { value: '50+', label: 'Happy Clients', icon: 'Users' },
            { value: '8+', label: 'Years Experience', icon: 'Award' },
            { value: '100+', label: 'Mentees Guided', icon: 'GraduationCap' }
          ]?.map((stat, index) => (
            <motion.div
              key={index}
              className="p-6 md:p-7 lg:p-8 rounded-2xl bg-gradient-to-br from-card/80 to-card/40 backdrop-blur-md border border-primary/20 shadow-xl"
              whileHover={{ y: -8, boxShadow: '0 20px 40px rgba(99, 102, 241, 0.3)' }}
              transition={{ duration: 0.3 }}
            >
              <div className="flex justify-center mb-3 md:mb-4">
                <div className="w-12 h-12 md:w-14 md:h-14 rounded-xl bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center">
                  <Icon name={stat?.icon} size={28} color="var(--color-primary)" />
                </div>
              </div>
              <div className="text-3xl md:text-4xl lg:text-5xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent mb-2">
                {stat?.value}
              </div>
              <div className="text-sm md:text-base lg:text-lg text-muted-foreground font-medium">
                {stat?.label}
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Scroll Indicator */}
        <motion.div
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2 hidden lg:block"
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <div className="flex flex-col items-center gap-2 text-muted-foreground">
            <span className="text-sm font-medium">Scroll to explore</span>
            <Icon name="ChevronDown" size={24} color="currentColor" />
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
};

export default HeroSection;